import {
  CSSProperties,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import styled from 'styled-components';
import { FixedSizeList } from 'react-window';

import { useObservable } from '@paradigm/utils/src/hooks';

import { useCounterpartiesService } from '@paradigm/features/src/unified-rfqs/contexts/counterparties';
import { Counterparty } from '@paradigm/features/src/unified-rfqs/entities/drfq/domain';
import {
  useRfqBuilderActions,
  useRfqBuilderView,
} from '@paradigm/features/src/unified-rfqs/ui';
import {
  useRfqFiltersFormActions,
  useRfqFiltersFormView,
} from '@paradigm/features/src/unified-rfqs/ui/rfq-filters/form-context';

import LoadingSpinner from '@paradigm/design-system/src/components/LoadingSpinner';

import { createCpsFilter } from '@paradigm/features/src/unified-rfqs/services/counterparties/cps-filter';
import { useRfqBuilderModalState } from '@paradigm/features/src/unified-rfqs/ui/rfq-builder/modal-context';
import useIsMobile from '@paradigm/design-system/src/hooks/useIsMobile';
import { logException } from '@paradigm/logging';
import CpItem from './CpItem';
import { CpsCtxMenuProvider } from './cps-context-menu';

interface Props {
  mode: 'builder' | 'filter';
}

interface CpsState {
  kind: 'initial' | 'loading' | 'error' | 'loaded';
  message?: string;
}

interface CpsListContentProps {
  state: {
    venueCode: string | undefined;
    onSelection: (selectedCps: readonly Counterparty[]) => void;
    prefill: { counterparties?: readonly Counterparty[] };
  };
  cpsState: CpsState;
}

function CounterpartyListRfqFilter() {
  const filterActions = useRfqFiltersFormActions();
  const filterView = useRfqFiltersFormView();
  const cpsService = useCounterpartiesService();
  const cpsState = useObservable(cpsService.state);
  const cpsFilter = useMemo(() => createCpsFilter(cpsService), [cpsService]);
  const initializedRef = useRef(false);

  const onSelection = useCallback(
    (selectedCps: readonly Counterparty[]) => {
      if (!initializedRef.current) return;

      const options = selectedCps.map((cp) => ({
        value: cp.desk_name,
        label: `${cp.firm_name} - ${cp.desk_name}`,
      }));
      filterActions.selectValues('counterparties', options);
    },
    [filterActions],
  );

  useEffect(() => {
    if (cpsState.kind !== 'loaded') return;
    if (initializedRef.current) return; // already initialized

    const currentSelections = filterView.counterparties.selectedOptions;
    cpsFilter.selectDeskNames(currentSelections.map((opt) => opt.value));
    initializedRef.current = true;
  }, [cpsState.kind, cpsFilter, filterView.counterparties.selectedOptions]);

  useEffect(() => {
    return cpsService.selected.observe(onSelection);
  }, [cpsService, onSelection]);

  const state = {
    venueCode: filterView.venues.selectedOptions[0]?.value,
    onSelection,
    prefill: { counterparties: [] },
  };

  return <CpsListContent state={state} cpsState={cpsState} />;
}

function CounterpartyListRfqBuilder() {
  const builderActions = useRfqBuilderActions();
  const { prefill } = useRfqBuilderModalState();
  const { venue } = useRfqBuilderView();
  const cpsService = useCounterpartiesService();
  const cpsState = useObservable(cpsService.state);
  const cpsFilter = useMemo(() => createCpsFilter(cpsService), [cpsService]);

  const state = {
    venueCode: venue.selectedOption.value,
    onSelection: builderActions.counterpartiesSelection,
    prefill,
  };

  // Fetch counterparties when venue changes
  useEffect(() => {
    if (!state.venueCode) return;
    cpsService.fetchCps(state.venueCode).catch((error) => {
      logException('Failed to fetch counterparties:', error);
    });
  }, [cpsService, state.venueCode]);

  useEffect(() => {
    // Only handle prefill when we have a venue and data is loaded
    if (!state.venueCode || cpsState.kind !== 'loaded') return;

    const cps = state.prefill.counterparties ?? [];
    if (cps.length > 0) {
      cpsFilter.selectDeskNames(cps.map((cp) => cp.desk_name));
    } else {
      cpsFilter.selectAllLpsOnly();
    }
  }, [state.venueCode, cpsState.kind, cpsFilter, state.prefill.counterparties]);

  useEffect(() => {
    return cpsService.selected.observe(state.onSelection);
  }, [state.onSelection, cpsService.selected]);

  return <CpsListContent state={state} cpsState={cpsState} />;
}

function CpsListContent({ cpsState }: CpsListContentProps) {
  if (cpsState.kind === 'error') {
    return (
      <CpsCtxMenuProvider>
        <Container>
          <ErrorContainer>Error: {cpsState.message}</ErrorContainer>
        </Container>
      </CpsCtxMenuProvider>
    );
  }

  if (cpsState.kind === 'initial' || cpsState.kind === 'loading') {
    return (
      <CpsCtxMenuProvider>
        <Container>
          <LoadingSpinner
            size={24}
            css="align-self: center; margin-top: 1.5rem"
          />
        </Container>
      </CpsCtxMenuProvider>
    );
  }

  return (
    <CpsCtxMenuProvider>
      <Container aria-label="counterparties list">
        <List />
      </Container>
    </CpsCtxMenuProvider>
  );
}

// Main component that switches between modes
export default function CpsList({ mode }: Props) {
  return mode === 'filter' ? (
    <CounterpartyListRfqFilter />
  ) : (
    <CounterpartyListRfqBuilder />
  );
}

const List = memo(function List() {
  const cpsService = useCounterpartiesService();
  const cpsIds = useObservable(cpsService.search.results);
  const isMobile = useIsMobile();
  const itemSize = isMobile ? 81 : 61;
  return (
    <FixedSizeList
      height={588}
      width="100%"
      itemSize={itemSize}
      itemData={cpsIds}
      itemCount={cpsIds.length}
      itemKey={getItemKey}
    >
      {ItemRenderer}
    </FixedSizeList>
  );
});

function ItemRenderer(props: {
  readonly data: readonly string[];
  readonly index: number;
  readonly style: CSSProperties;
}) {
  const { data, index, style } = props;
  const id = data[index]!;

  return (
    <Row style={style}>
      <CpItem id={id} />
    </Row>
  );
}

function getItemKey(index: number, data: readonly string[]) {
  return data[index]!;
}

const Container = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 0 calc(-1 * var(--h-pad));
  flex: 1;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const ErrorContainer = styled.pre`
  color: crimson;
  padding: 0 var(--h-pad);
  white-space: pre-wrap;
  margin: 1rem 0 0;
`;

const Row = styled.div`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  :first-child {
    border-top-color: transparent;
  }
`;
